import React from 'react';
import { NavLink, Outlet, Navigate, Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, ClipboardList, FileText, MessageSquare,
  FileEdit, Newspaper, Mail, Search, Lock, Shield, Eye, LogOut, ArrowLeft, HardDrive, Building2, BarChart3,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const sections = [
  { to: '/admin', end: true, label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/users',      label: 'Users',       icon: Users },
  { to: '/admin/quotes',     label: 'Quotes',      icon: ClipboardList },
  { to: '/admin/policies',   label: 'Policies',    icon: FileText },
  { to: '/admin/messages',   label: 'Messages',    icon: MessageSquare },
  { to: '/admin/content',    label: 'Site Content', icon: FileEdit },
  { to: '/admin/blog',       label: 'News / Blog', icon: Newspaper },
  { to: '/admin/insurers',   label: 'Insurers',    icon: Building2 },
  { to: '/admin/campaigns',  label: 'Campaigns',   icon: BarChart3 },
  { to: '/admin/newsletter', label: 'Newsletter',  icon: Mail },
  { to: '/admin/seo',        label: 'SEO',         icon: Search },
  { to: '/admin/storage',    label: 'Storage',     icon: HardDrive },
  { to: '/admin/security',   label: 'Security',    icon: Lock },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="container-x flex items-center justify-between h-[72px]">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
              <Eye size={18} />
            </span>
            <div>
              <p className="font-bold text-[#0b1f3a] leading-tight">Webmaster</p>
              <p className="text-[11px] text-slate-500 leading-tight">EasySpanishInsurance</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-slate-600 hover:text-[#0d3a8a] inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to site
            </Link>
            <Link to="/dashboard" className="text-sm text-slate-600 hover:text-[#0d3a8a]">User dashboard</Link>
            <button onClick={() => { logout(); navigate('/'); }} className="btn-outline !py-2 !px-4 text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="bg-[#f7fafd] border-b border-slate-100">
        <div className="container-x py-8">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
            <Shield size={14} /> Admin console
          </div>
          <h1 className="mt-1.5 text-2xl md:text-3xl font-bold text-[#0b1f3a] tracking-tight">Welcome back, {user.name}</h1>
        </div>
      </div>

      <div className="container-x py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-6">
          <aside className="bg-white border border-slate-200 rounded-2xl p-3 h-fit sticky top-24">
            <nav className="flex flex-col gap-1">
              {sections.map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  end={s.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-[#eef4fb] text-[#0d3a8a] font-semibold'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <s.icon size={16} />
                  {s.label}
                </NavLink>
              ))}
            </nav>
          </aside>
          <main className="min-w-0"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
